import { Box, Chip, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomTable from '@Common/CustomTable';
import useBreakPoinst from '@hooks/useBreakPoinst';
import { SectionTableWrapper } from '../../../../components/OwnerManagement/SectionTable/styles';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import type { IAutomationItem } from '../../types';
import TasksEmptyState from './TasksEmptyState';
import TasksErrorState from './TasksErrorState';
import TasksLoadingSkeleton from './TasksLoadingSkeleton';

interface ITasksTableProps {
  tasks: IAutomationItem[];
  totalRecords: number;
  isLoading: boolean;
  isError: boolean;
  activePage: number;
  perPage: string;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: string) => void;
  onEdit: (task: IAutomationItem) => void;
  onDelete: (task: IAutomationItem) => void;
  onCreate: () => void;
  onRetry: () => void;
}

const TasksTable = ({
  tasks,
  totalRecords,
  isLoading,
  isError,
  activePage,
  perPage,
  onPageChange,
  onPerPageChange,
  onEdit,
  onDelete,
  onCreate,
  onRetry,
}: ITasksTableProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { isPhone } = useBreakPoinst();
  const isRtl = language === 'ar';

  const columns = [
    {
      field: 'name',
      headerName: t('Automation.taskName'),
      flex: 1.5,
      renderCell: (params: any) => (
      <div className="flex items-center justify-start h-full">
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '14px',
              color: '#331860',
              fontFamily: 'Tajawal, sans-serif',
              cursor: 'pointer',
              textAlign: 'start'
            }}
            onClick={() => onEdit(params.row)}
          >
            {params.row.name}
          </Typography>
        </div>
      ),
    },
    {
      field: 'taskType',
      headerName: t('Automation.taskType'),
      flex: 1,
      renderCell: (params: any) => {
        const label =
          params.row.serviceName ||
          (isRtl ? params.row.sentTimeAr : params.row.sentTimeEn);
        if (!label) return null;
        return (
          <div className="flex items-center justify-start h-full">
            <Chip
              label={label}
              sx={{
                backgroundColor: '#F5F9FE',
                color: '#3087E9',
                fontFamily: 'Tajawal, sans-serif',
                fontSize: '12px',
                fontWeight: 400,
                borderRadius: '50px',
              }}
              />
          </div>
        );
      },
    },
    {
      field: 'assignedTo',
      headerName: t('Automation.assignedTo'),
      flex: 1,
      renderCell: (params: any) => (
                  <div className="flex items-center justify-start h-full">

        <Typography
          sx={{
            fontSize: '12px',
            color: '#000',
            fontFamily: 'Tajawal, sans-serif',
          }}
        >
          {params.row.serviceProviderName ?? '-'}
        </Typography>
        </div>
      ),
    },
    {
      field: 'modifiedAt',
      headerName: t('Automation.lastModified'),
      flex: 1,
      renderCell: (params: any) => (
        <div className="flex items-center justify-start h-full">
        <Typography
          sx={{
            fontSize: '12px',
            color: '#435363',
            fontFamily: 'Tajawal, sans-serif',
            textAlign: 'start'
          }}
        >
          {params.row.modifiedAt
            ? new Date(params.row.modifiedAt).toLocaleDateString('en-CA')
            : '-'}
        </Typography>
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params: any) => (
        <Box className="flex items-center gap-1 h-full">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(params.row);
            }}
            sx={{ color: '#435363' }}
          >
            <FiEdit2 size={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(params.row);
            }}
            sx={{ color: '#E74C3C' }}
          >
            <FiTrash2 size={16} />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (isLoading) {
    return <TasksLoadingSkeleton />;
  }

  if (isError) {
    return <TasksErrorState onRetry={onRetry} />;
  }

  if (tasks.length === 0) {
    return <TasksEmptyState onCreate={onCreate} />;
  }

  return (
    <SectionTableWrapper>
      <CustomTable
        showFilterBar={false}
        rows={tasks}
        totalRecords={totalRecords}
        columns={columns}
        loading={isLoading}
        perPage={perPage}
        activePage={activePage}
        totalPages={Math.ceil(totalRecords / Number(perPage)) || 1}
        setActivePage={(page: any) => onPageChange(page)}
        onPerpageChange={(val: any) => onPerPageChange(val)}
        listPerPagesNumber={[
          { label: '10', value: '10' },
          { label: '20', value: '20' },
          { label: '50', value: '50' },
        ]}
        getRowId={(row: IAutomationItem) => row.id}
        autoHeight
      />
    </SectionTableWrapper>
  );
};

export default TasksTable;
